// scanPhotos.ts — the photograph side of scan-to-stock.
//
// Mannie's framing, verbatim: "I want to be able to photograph the PO. I don't
// want to have to scan it in and transfer it to my pc and then upload it." So
// the phone IS the scanner, and this module holds the two things that makes
// true: what counts as a page, and how a 12-megapixel camera photo becomes
// something you can upload from a warehouse on one bar of signal.

/**
 * Pages of one purchase order. Matches MAX_SCAN_PAGES in
 * api/src/procurement/po-import-router.js and MAX_IMAGE_PAGES in
 * po-import-ai.js — the server refuses a ninth, this stops you taking one.
 */
export const MAX_SCAN_PAGES = 8;

/**
 * THE ACCEPT LIST IS NARROW ON PURPOSE. DO NOT "FIX" IT TO image/*.
 *
 * An iPhone shoots HEIC. Given `accept="image/*"` it hands the browser the HEIC
 * file untouched, and neither our reader nor Anthropic's vision blocks can read
 * one — the photo fails with a message nobody can act on. Given an accept list
 * of concrete types it cannot satisfy, iOS transcodes to JPEG during the pick.
 * The narrowness is the feature.
 */
export const SCAN_PHOTO_ACCEPT = 'image/jpeg,image/png';

/** Everything the server will read as a photographed page. */
const SCAN_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const SCAN_IMAGE_EXT = /\.(jpe?g|png|webp)$/i;

/**
 * Is this file one photographed page?
 *
 * Extension first, mimetype second — the same order the server uses, and for
 * the same reason: phones and browsers disagree about image mime types and send
 * application/octet-stream often enough to matter.
 */
export function isScanPhoto(file: File): boolean {
  if (SCAN_IMAGE_EXT.test(file.name)) return true;
  return !file.name.includes('.') && SCAN_IMAGE_TYPES.includes(file.type);
}

// A letter-size purchase order at 2000px on the long edge is about 170 dpi:
// far more than enough to read a part number, and about a tenth the bytes of
// what a modern phone camera produces. The model downsamples past this anyway.
const MAX_EDGE = 2000;
const JPEG_QUALITY = 0.85;

/**
 * Shrink a camera photo before it goes up the wire.
 *
 * This is a UX fix before it is a size fix: the person doing this is standing
 * at the receiving bench on whatever signal the warehouse has, and four 4 MB
 * photos is a wait long enough to make the flow feel broken. It also keeps
 * every page comfortably under the server's 5 MB per-image ceiling.
 *
 * FAILS OPEN, always: no canvas, no createImageBitmap, a decode error, or a
 * re-encode that came out bigger, and the original file goes up unchanged. A
 * photo that uploads slowly beats a photo that does not upload.
 *
 * The RENAME to .jpg is load-bearing, not tidiness: the server picks the image
 * media type off the extension, so a .png name carrying re-encoded JPEG bytes
 * would be announced to the model as a PNG and rejected.
 */
export async function shrinkPhoto(file: File): Promise<File> {
  if (typeof createImageBitmap !== 'function' || typeof document === 'undefined') return file;
  let bitmap: ImageBitmap | null = null;
  try {
    bitmap = await createImageBitmap(file);
    const longEdge = Math.max(bitmap.width, bitmap.height);
    if (!longEdge) return file;

    const scale = Math.min(1, MAX_EDGE / longEdge);
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    // A fresh canvas is transparent black and JPEG has no alpha channel, so a
    // PNG with transparency (a cropped or annotated PO, not a camera photo)
    // would encode as a solid black page. Paper is white; paint it first.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(bitmap, 0, 0, width, height);

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob((b) => resolve(b), 'image/jpeg', JPEG_QUALITY);
    });
    if (!blob || blob.size >= file.size) return file;
    // A UA that cannot encode the requested type falls back to PNG rather than
    // failing. Stamping .jpg on PNG bytes would announce the wrong media type
    // to the model, because the server picks it off the extension.
    if (blob.type !== 'image/jpeg') return file;

    return new File([blob], `${file.name.replace(/\.[^.]*$/, '')}.jpg`, {
      type: 'image/jpeg',
      lastModified: file.lastModified,
    });
  } catch {
    return file;
  } finally {
    bitmap?.close?.();
  }
}
